import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

// MUI Components
import Popover from "@mui/material/Popover";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

// Icons
import CSVFileUpload from "../../../iconComponents/CSVFileUpload";

// Core Components
import MDCSecondaryTabs from "../../../core/MDCSecondaryTabs";
import MDCBadge from "../../../core/MDCBadge";
import MDCTooltip from "../../../core/MDCTooltip";

// Theme
import theme from "../../../../themes/mdc-theme";

// Inner Components
import CsvErrorUploadStatus from "./CsvErrorUploadStatus";
import CsvUploadingStatus from "./CsvUploadingStatus";

// Constants
import {
  UPLOADING_STATUS,
  ERROR_STATUS,
  PENDING_STATUS,
  SUCCESS_STATUS,
} from "../../../../constants/GlobalConstants";

export default function CSVExcelUploadImport() {
  const [anchorEl, setAnchorEl] = useState(null);

  const uploadedAndErrorFiles = useSelector(
    (state) => state.csvImportExportReducer?.uploadedAndErrorFiles
  );

  const uploadingCount =
    uploadedAndErrorFiles?.filter((file) => file.status === UPLOADING_STATUS)
      .length || 0;

  const uploadingErrorsCount =
    uploadedAndErrorFiles?.filter((file) => file.status === ERROR_STATUS)
      .length || 0;

  const uploadingSuccessCount =
    uploadedAndErrorFiles?.filter((file) => file.status === SUCCESS_STATUS)
      .length || 0;

  const isUploading = uploadingCount > 0;

  const isError = uploadingErrorsCount > 0;

  const isSuccess = uploadingSuccessCount > 0;

  const handleOpen = (e) =>
    (isUploading || isError || !isSuccess) && setAnchorEl(e.currentTarget);

  const handleClose = () => setAnchorEl(null);

  const open = Boolean(anchorEl);

  useEffect(() => {
    if (
      uploadedAndErrorFiles?.length === 0 ||
      isUploading === 0 ||
      isError === 0
    ) {
      setAnchorEl(false);
    }
  }, [uploadedAndErrorFiles, isUploading, isError, isSuccess]);

  const navItems = [
    {
      id: 0,
      tabTitle: `Hochladen ${uploadingCount > 0 ? `(${uploadingCount})` : ""}`,
      tabContent: <CsvUploadingStatus />,
    },
    {
      id: 1,
      tabTitle: `Fehler ${
        uploadingErrorsCount > 0 ? `(${uploadingErrorsCount})` : ""
      }`,
      tabContent: <CsvErrorUploadStatus isError />,
    },
  ];

  return (
    <>
      {(isUploading || isError || isSuccess) && (
        <MDCTooltip
          title={
            isUploading &&
            !open && (
              <span style={{ fontSize: "0.875rem" }}>
                <span>Excel Upload wurde gestartet.</span>
                <br />
                <span>Klicken Sie bitte hier für weitere Details.</span>
              </span>
            )
          }
          placement="bottom"
        >
          <MDCBadge
            status={
              isUploading
                ? ERROR_STATUS
                : isError
                ? PENDING_STATUS
                : SUCCESS_STATUS
            }
            badgeContent={
              isUploading ? (
                <CircularProgress color="inherit" size={14} />
              ) : isError ? (
                uploadingErrorsCount
              ) : isSuccess && !isError && !isUploading ? (
                <CheckCircleOutlineIcon color="inherit" size={14} />
              ) : (
                ""
              )
            }
            icon={
              <CSVFileUpload
                color={
                  isUploading
                    ? theme.palette.MDCColors.color23
                    : isError
                    ? theme.palette.MDCColors.color34
                    : theme.palette.MDCColors.errorSuccess
                }
                onClick={handleOpen}
              />
            }
          />

          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            slotProps={{
              paper: {
                elevation: 6,
                sx: { borderRadius: 2, width: "32rem", maxHeight: "30rem" },
              },
            }}
          >
            <Paper
              elevation={0}
              sx={{
                p: 2,
                display: "flex",
                gap: 2,
                alignItems: "center",
                bgcolor: "background.paper",
              }}
            >
              <Box sx={{ flexGrow: 1 }}>
                <MDCSecondaryTabs navItems={navItems} />
              </Box>
            </Paper>
          </Popover>
        </MDCTooltip>
      )}
    </>
  );
}
